






















































import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {debounce, throttle} from 'lodash';
import {SettingConstants} from '@/services/settings.service';
import BindAttrs from '@/utils/binds-attrs';
import AmetrasSimpleTextOutputTemplate from '@/fields/AmetrasSimpleTextOutputTemplate.vue';
import {FieldStyleContext} from '@/fields/field-style-context';
import {COLOR_SERVICE_INSTANCE} from '@/services/style/color.service';

type rulesFunction = (currentValue: string) => string | boolean;

/**
 * General Component for Fields of type TEXTAREA
 */
@Component({
  components: {AmetrasSimpleTextOutputTemplate},

  mixins: [BindAttrs]
})
export default class AmetrasTextAreaFieldTemplate extends Vue {

  @Prop() marginDefinition!: string;
  @Prop() paddingDefinition!: string;
  @Prop() value!: string;
  @Prop() debounce!: boolean;
  @Prop() readonly!: boolean;
  @Prop() customRules!: [rulesFunction | string | boolean];
  /**
   * Indicates that we should render a simple mode of this component (increases the rendering performance)
   */
  @Prop() simple!: boolean;
  /**
   * This is a flag indicating that the placeholder should not be cleared if readonly mode is active.
   */
  @Prop({
    default: true
  }) clearPlaceholderIfEmpty!: boolean;
  @Prop({
    default: false
  }) required!: boolean;
  @Prop({
    default: true
  }) autoGrow!: boolean;
  @Prop() rows!: number;
  @Prop() rowHeight!: number;
  /**
   * Indicates that all errors should be shown
   */
  @Prop() forceShowErrors!: boolean;
  @Prop() typeSpecificSettings!: DisguiseDto.TextAreaMonoSpaceGridFieldSpecificSettings;
  /**
   * any external error present
   */
  @Prop() error!: boolean;
  @Prop() styleContext!: FieldStyleContext;

  colorService = COLOR_SERVICE_INSTANCE;
  modifiedlisteners: Record<string, any> = {};
  internalValue: string | null = null;

  rules = [(currentValue: string) => {
    if (this.required && (this.forceShowErrors || !this.readonly) && (!currentValue || currentValue.length === 0)) {
      return 'Bitte geben Sie einen Wert an!';
    }
    if (this.typeSpecificSettings && this.typeSpecificSettings.maxRowChars && typeof currentValue === 'string') {
      const allLines = (currentValue || '').split('\n');
      for (const currentLine of allLines) {
        if (currentLine.length > this.typeSpecificSettings.maxRowChars) {
          return 'Maximale Zeilenlänge überschritten';
        }
      }
    }
    const notHasAnyExternalError = typeof this.error === 'boolean' ? !this.error : true;
    return notHasAnyExternalError; // if any error from outside or any local error, return false
  }];

  $refs!: {
    inputfield: Vue
  };

  private throttledCalculateTextareaHeight = throttle(this.calculateTextareaHeight, 100, {
    trailing: true
  });

  private originalInputListeners: any[] = [];
  private originalChangeListeners: any[] = [];

  private debounceInputFunction = debounce((newValue: string) => {
    this.publishInputEvent(newValue);
  }, SettingConstants.DEBOUNCE_INPUT_MS);

  private debounceChangeFunction = debounce((newValue: string) => {
    this.publishChangeEvent(newValue);
  }, SettingConstants.DEBOUNCE_INPUT_MS);


  @Watch('value') modelChanges(newValue: string) {
    if (newValue !== undefined) {
      this.internalValue = newValue;
    } else {
      this.internalValue = null;
    }
    if (this.autoGrow) {
      this.throttledCalculateTextareaHeight(this.internalValue);
    }
    this.refreshValidation();
  }

  @Watch('required') requiredChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      if (newValue === false) {
        this.refreshValidation();
      }
    }
  }

  @Watch('error') errorChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.refreshValidation();
    }
  }

  created() {
    if (this.$listeners.input) { // listeners$ comes from the BindAttrs mixin
      if (Array.isArray(this.$listeners.input)) {
        this.originalInputListeners = this.originalInputListeners.concat(this.$listeners.input);
      } else {
        this.originalInputListeners.push(this.$listeners.input);
      }
    }
    if (this.$listeners.change) { // listeners$ comes from the BindAttrs mixin
      if (Array.isArray(this.$listeners.change)) {
        this.originalChangeListeners = this.originalChangeListeners.concat(this.$listeners.change);
      } else {
        this.originalChangeListeners.push(this.$listeners.change);
      }
    }

    this.modifiedlisteners = {
      ...this.$listeners,
      input: (newValue: string) => {
        this.convertInputEvent(newValue);
      },
      change: (newValue: string) => {
        this.convertChangeEvent(newValue);
      }
    };
  }

  mounted() {
    if (this.value !== undefined) {
      this.internalValue = this.value;
    } else {
      this.internalValue = null;
    }
    if (this.autoGrow) {
      this.throttledCalculateTextareaHeight(this.internalValue);
    }
  }

  /**
   * Converts the original Value and finally publishes the value
   */
  private convertChangeEvent(newValue: string) {
    const convertedValue = newValue;
    if (this.debounce && this.debounceChangeFunction) {
      this.debounceChangeFunction(convertedValue);
    } else {
      this.publishChangeEvent(convertedValue);
    }
  }

  /**
   * Converts the original Value and finally publishes the value
   */
  private convertInputEvent(newValue: string) {
    const convertedValue = newValue;
    if (this.debounce && this.debounceInputFunction) {
      this.debounceInputFunction(convertedValue);
    } else {
      this.publishInputEvent(convertedValue);
    }

    if (this.autoGrow) {
      this.throttledCalculateTextareaHeight(newValue);
    }
  }

  /**
   * Publishes the value to the original change listeners
   */
  private publishChangeEvent(value: string) {
    for (const fnc of this.originalChangeListeners) {
      fnc(value);
    }
  }

  /**
   * Publishes the value to the original input listeners
   */
  private publishInputEvent(value: string) {
    for (const fnc of this.originalInputListeners) {
      fnc(value);
    }
  }

  /**
   * Temporarily fix for jumping scrollbars if v-textarea is used together with autogrow
   *
   * https://github.com/vuetifyjs/vuetify/issues/5314
   */
  private calculateTextareaHeight(newValue: string | null) {
    // 1. find textarea
    const els = this.$el.getElementsByTagName('textarea');
    if (els && els.length) {
      const vuetifyTextarea = els[0];
      // 2. textarea must have any parent
      if (vuetifyTextarea.parentElement) {
        // 3. apply position relative, to allow exact fitting using position absolute
        vuetifyTextarea.parentElement.setAttribute('style', 'position: relative;');
        // 4. build temp textarea fitting exactly
        const tempTextArea = document.createElement('textarea');
        tempTextArea.setAttribute('style', 'height: 0; position: absolute; top: 0; bottom: 0; left: 0; right: 0;');
        tempTextArea.value = newValue || '';
        // 5. apply to dom (invisible due to height 0)
        vuetifyTextarea.parentElement.appendChild(tempTextArea);
        // 6. get calculated scrollHeight
        const height = tempTextArea.scrollHeight;
        // 7. remove the temp textarea again
        vuetifyTextarea.parentElement.removeChild(tempTextArea);
        // 8. either configured rows * configured rowHeight as a minimum height
        const minHeight = (this.rows || 1) * (this.rowHeight || 24);
        // 9. finnally modify the original textarea
        vuetifyTextarea.setAttribute('style', `height: ${Math.max(minHeight, height)}px`);
      }
    }
  }

  private refreshValidation() {
    if (!this.simple) {
      const inputFieldRef = this.$refs.inputfield as any;
      if (inputFieldRef !== undefined) {
        inputFieldRef.validate();
      } else {
        throw new Error('Cannot refresh validation, the input field is not available');
      }
    }
  }

}

